import '@mui/x-date-pickers/themeAugmentation'
import { CalendarIcon, ExpandMoreIcon } from '@dhl-ui/icons'
import type { Tokens } from '@dhl-ui/theme'
import { spacing } from '../spacing'
import { type ComponentCreation } from './types'

export const createMuixCalendar = ({ palette, typography }: Pick<Tokens, 'palette' | 'typography'>) =>
  ({
    MuiPickersCalendarHeader: {
      defaultProps: {
        slots: {
          switchViewIcon: ExpandMoreIcon,
        },
      },
      styleOverrides: {
        root: {
          padding: spacing(0, 1.5, 0, 1),
          margin: spacing(0, 0, 0.5, 0),
        },
        label: {
          textTransform: 'capitalize',
          fontWeight: 'bold',
        },
      },
    },
    MuiDateCalendar: {
      styleOverrides: {
        // this is, unfortunately, the only way to handle the disabled state; many do not have the ownerState available
        root: ({ ownerState }) => {
          // TODO: maybe fix later, but this component comes from another mui library
          const isFluid = (ownerState as Record<string, unknown>)?.isFluid ?? false

          return {
            ...(isFluid ? { width: '100%', maxWidth: spacing(56) } : { width: spacing(36) }),

            height: ownerState.slots?.calendarHeader ? spacing(46) : spacing(38),
            maxHeight: ownerState.slots?.calendarHeader ? spacing(46) : spacing(38),

            ...(ownerState.disabled
              ? {
                  color: palette.action.disabled,
                  '& .MuiDateCalendar-viewTransitionContainer': {
                    color: palette.action.disabled,
                    '> div > div': {
                      // gotta love how to target; disable weekNumber label:
                      '> .MuiDayCalendar-header > .MuiDayCalendar-weekDayLabel, .MuiDayCalendar-weekNumberLabel': {
                        color: palette.action.disabled,
                      },
                      '> .MuiPickersSlideTransition-root > .MuiDayCalendar-monthContainer > .MuiDayCalendar-weekContainer > .MuiDayCalendar-weekNumber':
                        {
                          color: palette.action.disabled,
                        },
                    },
                  },
                }
              : {}),
          }
        },
      },
    },
    MuiDatePicker: {
      defaultProps: {
        slots: {
          openPickerIcon: CalendarIcon,
        },
      },
    },
    MuiPickersLayout: {
      styleOverrides: {
        contentWrapper: {
          padding: spacing(1.5),
        },
      },
    },
    MuiDayCalendar: {
      styleOverrides: {
        weekDayLabel: {
          ...(typography.body2 as Record<string, string | number>),
          width: spacing(4),
          height: spacing(4),
        },
        weekNumberLabel: {
          width: spacing(4),
          height: spacing(4),
        },
        weekNumber: {
          ...(typography.body2 as Record<string, string | number>),
          width: spacing(4),
          height: spacing(4),
        },
        weekContainer: {
          justifyContent: 'space-between',
          margin: spacing(0.5, 0),
        },
        header: {
          justifyContent: 'space-between',
        },
        slideTransition: {
          minHeight: spacing(28),
        },
      },
    },
    MuiMonthCalendar: {
      styleOverrides: {
        root: ({ ownerState }) => {
          const fluid = (ownerState as Record<string, unknown>)?.fluid ?? false
          return {
            width: fluid ? '100%' : spacing(36),
            padding: 0,
          }
        },
      },
    },
    MuiYearCalendar: {
      styleOverrides: {
        root: ({ ownerState }) => {
          const fluid = (ownerState as Record<string, unknown>)?.fluid ?? false
          return {
            width: fluid ? '100%' : spacing(36),
            padding: 0,
          }
        },
      },
    },
    MuiDateRangePickerDay: {
      styleOverrides: {
        root: {
          '&.MuiDateRangePickerDay-rangeIntervalDayHighlight': {
            backgroundColor: palette.primary.light,
          },
        },
        rangeIntervalPreview: {
          border: 'none',
          margin: spacing(0, 0.5),
        },
      },
    },
    MuiDateRangePickerToolbar: {
      styleOverrides: {
        root: {
          padding: spacing(2, 0, 0, 1.5),
        },
        container: {
          alignItems: 'center',
        },
      },
    },
    MuiPickersToolbarText: {
      styleOverrides: {
        root: {
          ...(typography.h4 as Record<string, string | number>),
          textTransform: 'capitalize',
        },
      },
    },
    MuiPickersArrowSwitcher: {
      defaultProps: {
        slotProps: {
          nextIconButton: {
            size: 'small',
          },
          previousIconButton: {
            size: 'small',
          },
        },
      },
      styleOverrides: {
        root: {
          marginRight: spacing(-1.5),
        },
        spacer: {
          width: spacing(0.75),
        },
        button: {
          '&.Mui-disabled': {
            background: 'none',
          },
        },
      },
    },
    MuiPickersDay: {
      styleOverrides: {
        root: {
          width: spacing(4),
          height: spacing(4),
          ...(typography.body2 as Record<string, string | number>),
          '&.Mui-selected, &:focus.Mui-selected, &:hover.Mui-selected': {
            backgroundColor: palette.primary.main,
            color: palette.primary.contrastText,
            '&.Mui-disabled': {
              backgroundColor: palette.action.disabled,
              color: 'inherit',
            },
          },
          '&.MuiPickersDay-dayOutsideMonth:not(.Mui-selected), &.Mui-disabled:not(.Mui-selected)': {
            color: palette.action.disabled,
          },
          '&.MuiPickersDay-today': {
            borderColor: palette.dark.main,
          },
        },
      },
    },
    MuiPickersMonth: {
      styleOverrides: {
        root: {
          '.MuiPickersMonth-monthButton': {
            ...(typography.body2 as Record<string, string | number>),
            '&.Mui-selected, &:focus.Mui-selected, &:hover.Mui-selected': {
              backgroundColor: palette.primary.main,
              color: palette.primary.contrastText,
            },
          },
        },
      },
    },
    MuiPickersYear: {
      styleOverrides: {
        root: {
          '.MuiPickersYear-yearButton': {
            ...(typography.body2 as Record<string, string | number>),
            '&.Mui-selected, &:focus.Mui-selected, &:hover.Mui-selected': {
              backgroundColor: palette.primary.main,
              color: palette.primary.contrastText,
            },
          },
        },
      },
    },
  }) satisfies ComponentCreation & {
    // reason: not injecting the x lib here for just the types
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    MuiDateRangePickerDay: any
    // reason: not injecting the x lib here for just the types
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    MuiDateRangePickerToolbar: any
  }
